import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {usePagedList} from '@/hooks/common/use-paged-list'
import type {UploadTarget} from '@/hooks/sgsst/evidencias/use-evidence'
import type {components} from '@/lib/api-schema'

export type Incapacidad = components['schemas']['IncapacidadRead']
export type IncapacidadCreate = components['schemas']['IncapacidadCreate']
export type IncapacidadOrigen = components['schemas']['IncapacidadOrigen']
export type IncapacidadEstado = components['schemas']['IncapacidadEstado']
type IncapacidadPage = components['schemas']['Page_IncapacidadRead_']

export const INCAPACIDADES_PAGE_SIZE = 20

// Only owner/responsable (backend-enforced) may report for someone else; no `employee_id` means myself.
export function useReportarIncapacidad(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: IncapacidadCreate) =>
      (await api.post<Incapacidad>(`/businesses/${businessId}/mi/incapacidades`, payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['incapacidades', businessId]})
      queryClient.invalidateQueries({queryKey: ['notifications']})
    },
  })
}

export function useCrearIncapacidadParaEmpleado(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (payload: IncapacidadCreate) =>
      (await api.post<Incapacidad>(`/businesses/${businessId}/sgsst/incapacidades`, payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['incapacidades', businessId]})
      queryClient.invalidateQueries({queryKey: ['notifications']})
    },
  })
}

export async function mintIncapacidadUploadTarget(
  businessId: string,
  incapacidadId: string,
  file: File
): Promise<UploadTarget> {
  return (
    await api.post<UploadTarget>(`/businesses/${businessId}/mi/incapacidades/${incapacidadId}/upload-target`, {
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
    })
  ).data
}

function useIncapacidadesInvalidation(businessId: string) {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({queryKey: ['incapacidades', businessId]})
}

export function useSubirCertificadoIncapacidad(businessId: string) {
  const invalidate = useIncapacidadesInvalidation(businessId)
  return useMutation({
    mutationFn: async (input: {incapacidadId: string; objectKey: string; file: File}) =>
      (
        await api.post<Incapacidad>(`/businesses/${businessId}/mi/incapacidades/${input.incapacidadId}/certificado`, {
          object_key: input.objectKey,
          filename: input.file.name,
          content_type: input.file.type,
          size_bytes: input.file.size,
        })
      ).data,
    onSuccess: invalidate,
  })
}

/** R6: 0-5 por persona y año, y ninguna se borra. */
const EMPLOYEE_INCAPACIDADES_PAGE_SIZE = 10

export function useEmployeeIncapacidades(businessId: string | undefined, employeeId: string | null, page = 1) {
  return usePagedList<Incapacidad>(
    ['incapacidades', businessId, 'employee', employeeId],
    businessId && employeeId ? `/businesses/${businessId}/employees/${employeeId}/incapacidades` : undefined,
    {page, search: ''},
    EMPLOYEE_INCAPACIDADES_PAGE_SIZE
  )
}

// Without `estado` the backend defaults to reportada.
export function useIncapacidadesQueue(businessId: string | undefined, estado?: IncapacidadEstado, page = 1) {
  return useQuery({
    queryKey: ['incapacidades', businessId, 'queue', estado ?? 'todas', page],
    queryFn: async () =>
      (
        await api.get<IncapacidadPage>(`/businesses/${businessId}/sgsst/incapacidades`, {
          params: {estado, page, page_size: INCAPACIDADES_PAGE_SIZE},
        })
      ).data,
    enabled: !!businessId,
    placeholderData: (previous) => previous,
  })
}

export function useValidarIncapacidad(businessId: string) {
  const invalidate = useIncapacidadesInvalidation(businessId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (incapacidadId: string) =>
      (await api.post<Incapacidad>(`/businesses/${businessId}/sgsst/incapacidades/${incapacidadId}/validar`)).data,
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({queryKey: ['notifications']})
    },
  })
}

export function useRechazarIncapacidad(businessId: string) {
  const invalidate = useIncapacidadesInvalidation(businessId)
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {incapacidadId: string; motivo_rechazo: string}) =>
      (
        await api.post<Incapacidad>(`/businesses/${businessId}/sgsst/incapacidades/${input.incapacidadId}/rechazar`, {
          motivo_rechazo: input.motivo_rechazo,
        })
      ).data,
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({queryKey: ['notifications']})
    },
  })
}
